/**
 * The scheduler tick (R9) — implementing the contract this module has carried
 * since Phase 1: select due events in stable order, re-check firing
 * conditions, apply effects, allow chained scheduling, record processed and
 * fired ids.
 *
 * @remarks
 * Runs at step 6 of every EXECUTED turn (rejections consume no time — the v0
 * policy holds, and `wait` is the player's lever for letting the clock run).
 * An occurrence is DUE when its `dueTurn` is at or before the turn being
 * committed; due occurrences fire in (`dueTurn`, `sequenceId`) order, which
 * the replay harness depends on.
 *
 * **Conditions are re-checked at fire time and a failing event DROPS.**
 * Scheduling captured an intention; the world may have moved. A dropped
 * occurrence lands in `processedEventIds` but not `firedEventIds`, so the
 * audit trail tells fired from dropped and a replay can pin the difference.
 *
 * **Chains run inside the tick, and terminate by construction.** An event's
 * effects may `schedule_event` — including itself — and anything newly due is
 * processed in the same tick, EXCEPT that each definition fires at most once
 * per tick: a `delayTurns: 0` self-chain therefore leaves its next occurrence
 * pending for the next executed turn rather than spinning. That guard is the
 * only cycle-breaking rule, and it is a scheduling semantic ("once per turn"),
 * not a defensive cap.
 *
 * Event effects run through the SAME `applyEffect` the handlers use, so
 * everything an action can do, time can do — including `append_narrative`,
 * which is an event's voice (effects-only events, ruled with the R9 plan
 * sign-off).
 *
 * **The tick runs twice in a turn, and the second run is usually empty
 * (D188).** An event declaring `resolvesAt: 'end_of_turn'` is a deadline
 * that yields to the act that beats it: the step-6 tick leaves its due
 * occurrence pending, the triggers judge the turn, and a second tick at the
 * end of the turn fires whatever is STILL due — so a trigger that canceled
 * the occurrence, or made its conditions false, on the very turn it was due
 * has won. Mike's ruling (2026-09-17), after Calder measured the gas: a
 * player who closes the door on the kill turn should live. Each run fires
 * ONLY the occurrences that belong to it — a step-6 event a late event
 * schedules due stays pending until the next turn's tick, exactly as a
 * `delayTurns: 0` self-chain does — so "at most once per turn" holds for
 * every definition across both runs, and a pack with no yielding deadline
 * finds the second run empty.
 *
 * @packageDocumentation
 */

import type { EventDefinitionId, EventResolution, RuntimeState, RuntimeTranscriptEntry } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { TranscriptCursor } from './transcript-cursor.js';

import { evaluateCondition } from '../conditions/evaluate-condition.js';
import { applyEffects } from '../effects/apply-effect.js';

/** What one tick did: entries spoken, cursor advanced, fired ids, and the state after. */
export interface SchedulerTickResult {
	/** The advanced cursor, for anything stamped after the tick. */
	readonly cursor: TranscriptCursor;
	/** Transcript entries the tick's events spoke, in firing order. */
	readonly entries: readonly RuntimeTranscriptEntry[];
	/** The events that FIRED this tick (condition-dropped occurrences are processed, not fired). */
	readonly firedEventIds: readonly EventDefinitionId[];
	readonly state: RuntimeState;
}

/**
 * Whether a due occurrence belongs to this run of the tick (D188): the
 * deadlines that yield fire only at the end of the turn, everything else
 * only at step 6. A definition the bundle does not carry belongs to step 6,
 * where the loop drops it as processed — the corrupt-bundle posture below.
 *
 * @param resolvesAt - When the occurrence's definition says it fires
 * @param phase - Which run of the tick is asking
 * @returns Whether this run may fire it
 */
const isDueIn = (resolvesAt: EventResolution | undefined, phase: EventResolution): boolean =>
	resolvesAt === 'end_of_turn' ? phase === 'end_of_turn' : phase === 'tick';

/**
 * Run the scheduler tick for one executed turn.
 *
 * @param bundle - The loaded bundle (event definitions, condition context)
 * @param state - The state after the action's effects
 * @param cursor - The turn's transcript cursor, already past the action's entries
 * @param phase - Step 6's tick (`'tick'`, the default) or the end-of-turn run (D188)
 * @returns The state after every due occurrence, the entries spoken, and the fired ids
 */
export const tickScheduler = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	cursor: TranscriptCursor,
	phase: EventResolution = 'tick',
): SchedulerTickResult => {
	const thisTurn = state.progression.turnCount + 1;
	const firedThisTick = new Set<string>();
	const firedEventIds: EventDefinitionId[] = [];
	const entries: RuntimeTranscriptEntry[] = [];

	let workingState = state;
	let workingCursor = cursor;

	// The loop re-selects after every firing because an event's effects may
	// have scheduled MORE due occurrences (chains). Termination: each pass
	// either fires a definition not yet fired this tick (a finite set) or
	// finds nothing eligible and stops.
	for (;;) {
		const dueOccurrence = [...workingState.scheduler.pendingEvents]
			.filter(
				(pending) =>
					pending.dueTurn <= thisTurn &&
					!firedThisTick.has(pending.eventDefinitionId) &&
					isDueIn(bundle.indexes.eventsById.get(pending.eventDefinitionId)?.resolvesAt, phase),
			)
			.sort((left, right) => left.dueTurn - right.dueTurn || left.sequenceId - right.sequenceId)[0];

		if (dueOccurrence === undefined) {
			return { cursor: workingCursor, entries, firedEventIds, state: workingState };
		}

		// Consume the occurrence and record it processed either way.
		workingState = {
			...workingState,
			scheduler: {
				...workingState.scheduler,
				pendingEvents: workingState.scheduler.pendingEvents.filter((pending) => pending !== dueOccurrence),
				processedEventIds: [...workingState.scheduler.processedEventIds, dueOccurrence.eventDefinitionId],
			},
		};

		const eventDefinition = bundle.indexes.eventsById.get(dueOccurrence.eventDefinitionId);

		// An undefined definition cannot happen through the loader (semantic
		// validation refuses undeclared event ids), so a miss is a corrupt
		// bundle and the occurrence simply drops — same defensive posture as
		// the catalog walks.
		const hasMetConditions =
			eventDefinition !== undefined &&
			eventDefinition.conditions.every((condition) => evaluateCondition(condition, bundle, workingState));

		if (!hasMetConditions) {
			continue;
		}

		firedThisTick.add(dueOccurrence.eventDefinitionId);
		firedEventIds.push(dueOccurrence.eventDefinitionId);
		workingState = {
			...workingState,
			scheduler: {
				...workingState.scheduler,
				firedEventIds: [...workingState.scheduler.firedEventIds, dueOccurrence.eventDefinitionId],
			},
		};

		// R11: an event's lines carry the event's own name — the transcript
		// records WHO spoke, not just that time did.
		const applied = applyEffects(eventDefinition.effects, bundle, workingState, workingCursor, `event:${dueOccurrence.eventDefinitionId}`);

		workingState = applied.state;
		workingCursor = applied.cursor;
		entries.push(...applied.entries);
	}
};
